import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { updateUserNotificationSettings } from '../services/api';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DIRECT_APP_URL = 'https://lively-dune-036b62e0f.4.azurestaticapps.net';

interface InstallPromptProps {
  /** When provided, accepting the prompt with location granted will persist locationConsent=true server-side for this resource. */
  resourceId?: number | null;
}

export default function InstallPrompt({ resourceId }: InstallPromptProps = {}) {
  const { isEmbedMode } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [installing, setInstalling] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if user previously dismissed (try localStorage, fall back to sessionStorage
    // for embed mode where third-party localStorage may be blocked)
    try {
      const dismissed = localStorage.getItem('autotask-hub-install-dismissed');
      if (dismissed) return;
    } catch {
      // localStorage blocked in iframe - fall through to sessionStorage check
    }
    try {
      const sessionDismissed = sessionStorage.getItem('autotask-hub-install-dismissed');
      if (sessionDismissed) return;
    } catch {
      // sessionStorage also blocked - fall through
    }

    if (isEmbedMode) {
      // In iframe: can't get beforeinstallprompt, show our own prompt
      // with a link to open the app directly
      setShowPrompt(true);
      return;
    }

    // Direct access: listen for native install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isEmbedMode]);

  const handleInstall = useCallback(async () => {
    if (isEmbedMode) {
      // Open the app in a new tab so the browser can trigger the install prompt
      window.open(DIRECT_APP_URL, '_blank');
      setShowPrompt(false);
      // Persist dismiss so prompt doesn't reappear on refresh
      const ts = Date.now().toString();
      try { localStorage.setItem('autotask-hub-install-dismissed', ts); } catch { /* blocked */ }
      try { sessionStorage.setItem('autotask-hub-install-dismissed', ts); } catch { /* blocked */ }
      return;
    }

    if (!deferredPrompt.current) return;

    setInstalling(true);

    try {
      // Request location permission first
      const locPerm = await navigator.permissions.query({ name: 'geolocation' });
      if (locPerm.state === 'prompt') {
        // Trigger the actual permission dialog
        await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
          });
        }).catch(() => {
          // User denied location - that's handled below
        });
      }

      // Check if location was granted
      const locPermAfter = await navigator.permissions.query({ name: 'geolocation' });
      if (locPermAfter.state === 'denied') {
        // Location denied - don't install
        setInstalling(false);
        return;
      }

      // Trigger the install prompt
      await deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;

      if (outcome === 'accepted' && resourceId) {
        // Persist locationConsent server-side so the location hook actually starts tracking.
        // If resourceId isn't known yet (rare edge case), user can opt in from User Settings.
        try {
          await updateUserNotificationSettings(resourceId, { locationConsent: true });
        } catch (saveErr) {
          console.warn('Failed to persist locationConsent post-install:', saveErr);
        }
      }

      deferredPrompt.current = null;
      setShowPrompt(false);
    } catch (err) {
      console.error('Install error:', err);
    } finally {
      setInstalling(false);
    }
  }, [isEmbedMode]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    const ts = Date.now().toString();
    try { localStorage.setItem('autotask-hub-install-dismissed', ts); } catch { /* blocked in iframe */ }
    try { sessionStorage.setItem('autotask-hub-install-dismissed', ts); } catch { /* blocked in iframe */ }
  }, []);

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
              AT
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Install AutoTask Hub</h2>
              <p className="text-sm text-slate-400">Get the full app experience</p>
            </div>
          </div>

          <div className="space-y-3 text-sm text-slate-300">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span>Get push notifications for new tickets and customer replies</span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Share your general location so dispatchers can route the closest tech</span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span>Works offline and launches like a native app</span>
            </div>
          </div>
        </div>

        {/* Embed mode notice */}
        {isEmbedMode && (
          <div className="mx-6 p-3 bg-blue-900/30 border border-blue-700/40 rounded-lg mb-2">
            <p className="text-xs text-blue-200">
              To install, AutoTask Hub will open in a new tab. Complete the install there, then come back here. Notifications will work even while using the HUD.
            </p>
          </div>
        )}

        {/* Location Consent */}
        <div className="mx-6 p-3 bg-amber-900/30 border border-amber-700/40 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-xs text-amber-200">
              <strong>Location Tracking:</strong> By installing, you agree to share your approximate location during work hours (Mon-Fri). Only general area is tracked — no GPS precision. Your team can see each other's locations on the Field Map to help with dispatch routing.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="p-6 pt-4 flex gap-3">
          <button
            onClick={handleDismiss}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleInstall}
            disabled={installing}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {installing ? 'Installing...' : isEmbedMode ? 'Open & Install' : 'Install App'}
          </button>
        </div>
      </div>
    </div>
  );
}
